
import {defineComponent, ref} from "vue";
import {defineRule} from "vee-validate";
import InputSelect from "@/components/UI/InputSelect.vue";
import {ContentVisibilityRuleRestDtoModel} from "@/models/api/pharma-cms-menu/ContentVisibilityRuleRestDtoModel";
import InputDate from "@/components/UI/InputDate.vue";
import {ProductFilterOverviewRestDto} from "@/api/pharma-cpc-mgmt/models";
import {mapActions, mapGetters} from "vuex";
import {CodeDescriptionRestDto} from "@/api/pharma-cms-content-block/models";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import {UIStateDto} from "@/dtos/UIStateDto";
import {AbilityContext} from "@/context/AbilityContext";
import {arrayHasContent} from "@/helpers/functions/arrays";
import {VisibilityRuleTypeEnum} from "@/api/enums/visibility-rule-type-enum";
import {ClientGroupManagementService} from "@/services/rest/idm-user/ClientGroupManagementService";
import I18n from "@/modules/i18n";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";


// Define custom VeeValidate rule for parent_code or dp_managed_parent_category_code
defineRule('clientGroupRequired', (value: string | null, {currentGroups}: { currentGroups: [] | null }) => {
  if (!currentGroups?.length) {
    return I18n.global.t('clientGroupRequired');
  }

  return true;
});

export default defineComponent({
  name: "VisibilityRule",
  emits: ["changed", "delete", "error"],
  components: {InputDate, InputSelect},
  props: {
    payload: {
      type: [ContentVisibilityRuleRestDtoModel, Object],
      required: true
    },
    visibilityRuleOptionsKeys: {
      type: Array,
      required: false
    },
    errors: {
      type: Object,
      required: true,
    },
    hideDelete: {
      type: Boolean,
      default: false,
    }
  },
  setup(props) {
    const visibilityRule = ref(JSON.parse(JSON.stringify(props.payload))) as ContentVisibilityRuleRestDtoModel;

    return {
      visibilityRule,
    };
  },
  data() {
    return {
      visibilityRuleUI: UIStateDto.createWithDefaults(),

      selectedClientGroupNames: null as string[] | null,
      clientGroups: null as CodeDescriptionRestDto[] | null,
      newClientGroupCode: '',
      visibilityRuleEnumValues: [] as CodeDescriptionRestDto[]
    }
  },
  watch: {
    payload: {
      handler(newPayload: ContentVisibilityRuleRestDtoModel) {
        // console.log('payload changed: ', newPayload);
        this.visibilityRule = ref(JSON.parse(JSON.stringify(newPayload)));
      },
      deep: true,
      immediate: false
    },
  },
  mounted() {
    this.reloadContent();
  },
  computed: {
    ...mapGetters('cpc_mgmt', ["getProductFilterOverview"]),
    VisibilityRuleTypeEnum() {
      return VisibilityRuleTypeEnum
    },
    clientGroupOptions(): CodeDescriptionRestDto[] {
      return this.clientGroups ?? [] as CodeDescriptionRestDto[];
    },
    namedProductFilterOptions(): CodeDescriptionRestDto[] {
      const namedProductFilterOptions = [] as CodeDescriptionRestDto[];

      if (AbilityContext.isAuthorizedForFeature('USE_NAMED_PRODUCT_FILTER')) {
        this.getProductFilterOverview.forEach((product_filter: ProductFilterOverviewRestDto) => {
          namedProductFilterOptions.push(
              {code: product_filter.code, description: product_filter.name}
          );
        });
      }

      return namedProductFilterOptions;
    },
    visibilityRuleOptions(): CodeDescriptionRestDto[] {
      let visibilityRuleOptions = this.visibilityRuleEnumValues;

      if (this.visibilityRuleOptionsKeys) {
        visibilityRuleOptions = visibilityRuleOptions
          .filter((option: CodeDescriptionRestDto) => this.visibilityRuleOptionsKeys!.includes(option.code));
      }

      if (AbilityContext.isNotAuthorizedForFeature('USE_NAMED_PRODUCT_FILTER')
        || !arrayHasContent(this.namedProductFilterOptions)) {
        return visibilityRuleOptions.filter((option: CodeDescriptionRestDto) => !option.code.includes('PRODUCT_FILTER'));
      }

      return visibilityRuleOptions;
    },
  },
  methods: {
    ...mapActions('cpc_mgmt', ["searchProductFilterOverview"]),
    async reloadContent(): Promise<void> {
      this.visibilityRuleUI.setNotReady();

      try {
        await Promise.all([
          EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.CONTENT_VISIBILITY_RULE, DpModuleEnum.CMS)
            .then((result: CodeDescriptionRestDto[]) => this.visibilityRuleEnumValues = result),
          AbilityContext.isAuthorizedForFeature('USE_NAMED_PRODUCT_FILTER')
            ? this.searchProductFilterOverview({is_named: true})
            : Promise.resolve(),
          ClientGroupManagementService.getInstance().findClientGroups()
            .then((result: CodeDescriptionRestDto[]) => this.clientGroups = result),
        ]);

        // Loop over this.visibilityRule.client_group_codes, look up the 'code' in this.clientGroups (i.code), if a match is found, add (i.description) to this.selectedClientGroupNames
        this.selectedClientGroupNames = this.visibilityRule.client_group_codes?.map(code =>
            this.clientGroups?.find(group => group.code === code)?.description || code
        ).filter(name => name) || [];

      } catch (exceptions: unknown) {
        this.$emit('error', exceptions);
      } finally {
        this.visibilityRuleUI.setReady();
      }
    },
    deleteVisibilityRule() {
      this.$emit('delete');
    },
    async addClientGroup(): Promise<void> {
      if (this.newClientGroupCode === '') {
        return;
      }
      if ((this.visibilityRule.client_group_codes ?? []).includes(this.newClientGroupCode)) {
        return;
      }
      this.visibilityRule.client_group_codes ??= [];
      this.selectedClientGroupNames ??= [];
      this.visibilityRule.client_group_codes?.push(this.newClientGroupCode);
      const clientGroupName = this.clientGroups?.find(group => group.code === this.newClientGroupCode)?.description || this.newClientGroupCode;
      this.selectedClientGroupNames?.push(clientGroupName);
      await (this.$refs.clientGroupField as any)?.validateField?.();
      this.onChange()
    },
    async removeClientGroup(i: number): Promise<void> {
      this.visibilityRule.client_group_codes ??= [];
      this.selectedClientGroupNames ??= [];
      this.visibilityRule.client_group_codes.splice(i, 1);
      this.selectedClientGroupNames.splice(i, 1);
      await (this.$refs.clientGroupField as any)?.validateField?.();
      this.onChange()
    },
    onChange() {
      switch (this.visibilityRule.type_code) {
        case VisibilityRuleTypeEnum.HIDE:
          this.visibilityRule.begin_date = null;
          this.visibilityRule.end_date = null;
          this.visibilityRule.product_filter_code = null;
          this.visibilityRule.client_group_codes = [];
          this.selectedClientGroupNames = [];
          break;
        case VisibilityRuleTypeEnum.VISIBLE_WITHIN_PERIOD:
          this.visibilityRule.product_filter_code = null;
          this.visibilityRule.client_group_codes = [];
          this.selectedClientGroupNames = [];
          break;
        case VisibilityRuleTypeEnum.PRODUCT_FILTER_NO_PRODUCTS:
        case VisibilityRuleTypeEnum.PRODUCT_FILTER_WITH_PRODUCTS:
          this.visibilityRule.begin_date = null;
          this.visibilityRule.end_date = null;
          this.visibilityRule.client_group_codes = [];
          this.selectedClientGroupNames = [];
          break;
        case VisibilityRuleTypeEnum.VISIBLE_FOR_CLIENT_GROUPS:
        case VisibilityRuleTypeEnum.HIDE_FOR_CLIENT_GROUPS:
          this.visibilityRule.begin_date = null;
          this.visibilityRule.end_date = null;
          this.visibilityRule.product_filter_code = null;
          break;
      }

      this.$emit('changed', this.visibilityRule);
    },
  }
});
